import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { activityService } from "../../../services/activityService";
import { GlobalActivityService } from "../../../services/globalActivityService";
import {
  ActivityType,
  ActivityVariableDefinition,
  GlobalActivityType,
} from "../../../types/activity";
import { getDefaultUnit } from "../../../utils/unitUtils";

/**
 * The athlete's activity library: custom activities plus the admin packs they
 * have added, de-duplicated by name (custom wins). Mirrors the loader inside
 * AddPerformanceLogForm so the Performance flow and Goal editor share it.
 */
export function useActivityLibrary(enabled: boolean) {
  const { currentUser, userData, authState } = useAuth();
  const isImperial = authState.user?.isImperial ?? false;
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [packTypes, setPackTypes] = useState<ActivityType[]>([]);
  const [loading, setLoading] = useState(false);
  const userPackIdsString = JSON.stringify(userData?.adminActivityCatIDs || []);

  const load = useCallback(async () => {
    if (!currentUser?.uid) return;
    try {
      setLoading(true);
      const types = await activityService.fetchActivityTypes(currentUser.uid);
      setActivityTypes(types);
      const userPackIds: string[] = JSON.parse(userPackIdsString);
      if (userPackIds.length > 0) {
        try {
          const globalTypes =
            await GlobalActivityService.fetchActivityTypesForCategories(
              userPackIds,
            );
          setPackTypes(
            globalTypes.map(
              (gt: GlobalActivityType): ActivityType => ({
                id: `global_${gt.categoryId}_${gt.id}`,
                name: gt.name,
                description: gt.description,
                variables: gt.variables.map((v): ActivityVariableDefinition =>
                  typeof v === "string"
                    ? { name: v, defaultUnit: getDefaultUnit(v, isImperial) }
                    : { ...v, defaultUnit: getDefaultUnit(v.name, isImperial) },
                ),
                image: gt.image,
                isFromPack: true,
                categoryId: gt.categoryId,
              }),
            ),
          );
        } catch (packError) {
          console.error("Error fetching pack activity types:", packError);
          setPackTypes([]);
        }
      } else {
        setPackTypes([]);
      }
    } catch (error) {
      console.error("Error fetching activity types:", error);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid, userPackIdsString, isImperial]);

  useEffect(() => {
    if (!enabled) return;
    load();
  }, [enabled, load]);

  const allActivityTypes = useMemo(() => {
    const names = new Set(activityTypes.map((a) => a.name.toLowerCase()));
    return [
      ...activityTypes,
      ...packTypes.filter((p) => !names.has(p.name.toLowerCase())),
    ];
  }, [activityTypes, packTypes]);

  return { activityTypes: allActivityTypes, loading, reload: load, isImperial };
}
